<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import type { WaveformData, TimeRange, EditMode } from '@/types'

const props = defineProps<{
  waveformData: WaveformData | null
  duration: number
  selection: TimeRange
  currentTime: number
  filename?: string
  editMode: EditMode
}>()

const emit = defineEmits<{
  (e: 'update:selection', selection: TimeRange): void
  (e: 'seek', time: number): void
}>()

// Canvas refs
const containerRef = ref<HTMLDivElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)

// RAF render coalescing - prevents multiple frames being queued
let renderPending = false
const scheduleRender = () => {
  if (renderPending) return
  renderPending = true
  requestAnimationFrame(() => {
    renderPending = false
    drawWaveform()
  })
}

// Canvas state
const canvasWidth = ref(800)
const canvasHeight = ref(150)
const dpr = ref(1)

// Interaction state
const isDragging = ref(false)
const dragTarget = ref<'start' | 'end' | 'region' | 'playhead' | null>(null)
const dragStartX = ref(0)
const dragStartSelection = ref<TimeRange>({ start: 0, end: 0 })
// For precise dragging - track the exact clientX and time at drag start
const dragStartClientX = ref(0)
const dragStartTime = ref(0)
// Track hovered marker for visual feedback
const hoveredMarker = ref<'start' | 'end' | 'playhead' | null>(null)
// Track hovered time label (for direct hover on labels)
const hoveredLabel = ref<'start' | 'end' | null>(null)
// Track which label is being edited
const editingLabel = ref<'start' | 'end' | null>(null)
const editInputValue = ref('')
const editInputRef = ref<HTMLInputElement | null>(null)
// Track label drag state (to distinguish click vs drag)
const labelDragPending = ref<'start' | 'end' | null>(null)
const labelDragStartX = ref(0)
const DRAG_THRESHOLD = 3 // pixels of movement before it becomes a drag

// Touch state

// Darken a hex color by a factor (0.65 = 65% brightness)
const darkenHex = (hex: string, factor: number): string => {
  const h = hex.replace('#', '')
  const r = Math.min(255, Math.round(parseInt(h.substring(0, 2), 16) * factor))
  const g = Math.min(255, Math.round(parseInt(h.substring(2, 4), 16) * factor))
  const b = Math.min(255, Math.round(parseInt(h.substring(4, 6), 16) * factor))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// Cached CSS color values (avoid getComputedStyle every frame)
const cachedColors = {
  waveformBg: '#edf2f7',
  normalColor1: '#a0aec0',
  normalColor2: '#a0aec0',
  selectedColor1: '#667eea',
  selectedColor2: '#667eea',
  markerColor: '#f56565',
  playheadColor: '#ef4444',
  // Pre-darkened versions for hover states (replaces expensive ctx.filter)
  markerColorHover: '#9f4242',
  playheadColorHover: '#9b2c2c',
}

const refreshCachedColors = () => {
  const style = getComputedStyle(document.documentElement)
  cachedColors.waveformBg = style.getPropertyValue('--waveform-background').trim() || '#edf2f7'
  cachedColors.normalColor1 = style.getPropertyValue('--waveform-color').trim() || '#a0aec0'
  cachedColors.normalColor2 = style.getPropertyValue('--waveform-color-secondary').trim() || '#a0aec0'
  cachedColors.selectedColor1 = style.getPropertyValue('--waveform-selected').trim() || '#667eea'
  cachedColors.selectedColor2 = style.getPropertyValue('--waveform-selected-secondary').trim() || '#667eea'
  cachedColors.markerColor = style.getPropertyValue('--marker-color').trim() || '#f56565'
  cachedColors.playheadColor = style.getPropertyValue('--error-color').trim() || '#ef4444'
  // Pre-compute darkened hover colors (equivalent to brightness(0.65))
  cachedColors.markerColorHover = darkenHex(cachedColors.markerColor, 0.65)
  cachedColors.playheadColorHover = darkenHex(cachedColors.playheadColor, 0.65)
}

// Round to one decimal place for consistent display
const roundToDecimal = (value: number, decimals: number = 1): number => {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}

// Format time helper - rounds to 1 decimal place for consistent display
const formatTime = (seconds: number): string => {
  // Round to 1 decimal place first for consistency
  const rounded = roundToDecimal(seconds, 1)
  const mins = Math.floor(rounded / 60)
  const secs = Math.floor(rounded % 60)
  const ms = Math.round((rounded % 1) * 10) % 10 // Use round and mod 10 to handle edge cases
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${ms}`
}

// Format duration without arrows (arrows will be handled by CSS lines)
// Uses same rounding as formatTime for consistency
const formatDuration = (seconds: number): string => {
  // Round to 1 decimal place first for consistency
  const rounded = roundToDecimal(seconds, 1)
  const mins = Math.floor(rounded / 60)
  const secs = Math.floor(rounded % 60)
  const ms = Math.round((rounded % 1) * 10) % 10 // Use round and mod 10 to handle edge cases

  // If less than 1 minute, show just seconds (without leading zero if < 10)
  if (mins === 0) {
    const secStr = secs < 10 ? String(secs) : String(secs).padStart(2, '0')
    return `${secStr}.${ms}`
  }

  // Otherwise show full MM:SS.m format
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}.${ms}`
}

// Calculate selection width as percentage
const selectionWidthPercent = computed(() => {
  return ((props.selection.end - props.selection.start) / props.duration) * 100
})

// Calculate label offset factor (0 = centered, 1 = fully pushed outward)
// Starts shifting when selection is < 15%, fully shifted at < 6%
const labelOffsetFactor = computed(() => {
  const width = selectionWidthPercent.value
  if (width >= 15) return 0  // No offset needed
  if (width <= 6) return 1   // Fully pushed out
  // Linear interpolation between 15% and 6%
  return 1 - (width - 6) / (15 - 6)
})

// Calculate start label transform (from -50% to -100%)
const startLabelTransform = computed(() => {
  const offset = labelOffsetFactor.value
  // From -50% (centered) to -100% (pushed left)
  const translateX = -50 - (offset * 50)
  return `translateX(${translateX}%)`
})

// Calculate end label transform (from -50% to 0%)
const endLabelTransform = computed(() => {
  const offset = labelOffsetFactor.value
  // From -50% (centered) to 0% (pushed right)
  const translateX = -50 + (offset * 50)
  return `translateX(${translateX}%)`
})

// Position duration label below time labels when selection is very narrow (< 5%)
const durationLabelPosition = computed(() => {
  return selectionWidthPercent.value < 4 ? 'below' : 'inline'
})

// Compute the displayed duration from rounded start and end values
// This ensures: displayed_duration = displayed_end - displayed_start (no 0.1 mismatch)
const displayedDuration = computed(() => {
  const roundedStart = roundToDecimal(props.selection.start, 1)
  const roundedEnd = roundToDecimal(props.selection.end, 1)
  return roundedEnd - roundedStart
})

// Computed

// Convert time to pixel position
const timeToPixel = (time: number): number => {
  return (time / props.duration) * canvasWidth.value
}

// Convert pixel position to time
const pixelToTime = (pixel: number): number => {
  return (pixel / canvasWidth.value) * props.duration
}

// Update canvas size
const updateCanvasSize = () => {
  if (!containerRef.value || !canvasRef.value) return

  refreshCachedColors()

  const rect = containerRef.value.getBoundingClientRect()
  dpr.value = window.devicePixelRatio || 1

  canvasWidth.value = rect.width
  canvasHeight.value = Math.max(100, Math.min(200, rect.width * 0.2))

  canvasRef.value.width = canvasWidth.value * dpr.value
  canvasRef.value.height = canvasHeight.value * dpr.value
  canvasRef.value.style.width = `${canvasWidth.value}px`
  canvasRef.value.style.height = `${canvasHeight.value}px`

  drawWaveform()
}

// Draw the waveform - crispy 1px lines, non-mirrored, normalized
const drawWaveform = () => {
  if (!canvasRef.value || !props.waveformData) return
  
  const canvas = canvasRef.value
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  ctx.scale(dpr.value, dpr.value)
  
  const width = canvasWidth.value
  const height = canvasHeight.value
  const centerY = height / 2
  
  // Clear canvas
  ctx.clearRect(0, 0, width, height)
  
  // Draw background
  ctx.fillStyle = cachedColors.waveformBg
  ctx.fillRect(0, 0, width, height)

  // Use min/max peaks for asymmetric waveform (like old version)
  const { minPeaks, maxPeaks, length: peakCount } = props.waveformData

  // Colors for the two halves of the waveform (cached from CSS variables)
  const { normalColor1, normalColor2, selectedColor1, selectedColor2 } = cachedColors

  ctx.lineWidth = 1

  // Scale factor to leave margin for filename overlay (80% of full height)
  const amplitudeScale = 0.8

  // Draw waveform using 1px line strokes (crispy like old version)
  // Non-mirrored: min values go up from center, max values go down from center
  for (let i = 0; i < peakCount; i++) {
    const x = (i / peakCount) * width - 0.5
    const peakTime = (i / peakCount) * props.duration
    
    const min = minPeaks[i]  // Negative values
    const max = maxPeaks[i]  // Positive values

    // Check if peak is in selection
    // In cut mode, selection is kept (highlighted). In trim mode, selection is removed (not highlighted)
    const inSelectionRange = peakTime >= props.selection.start && peakTime <= props.selection.end
    const isSelected = props.editMode === 'cut' ? inSelectionRange : !inSelectionRange
    
    // Draw bottom half (min values go UP from center because min is negative)
    ctx.beginPath()
    ctx.strokeStyle = isSelected ? selectedColor1 : normalColor1
    ctx.moveTo(x, centerY + (centerY * min * amplitudeScale) + 0.5)
    ctx.lineTo(x, centerY)
    ctx.stroke()
    
    // Draw top half (max values go DOWN from center because max is positive)
    ctx.beginPath()
    ctx.strokeStyle = isSelected ? selectedColor2 : normalColor2
    ctx.moveTo(x, centerY)
    ctx.lineTo(x, centerY + (centerY * max * amplitudeScale) + 0.5)
    ctx.stroke()
  }
  
  // Draw selection overlay masks based on edit mode
  const selStartX = timeToPixel(props.selection.start)
  const selEndX = timeToPixel(props.selection.end)

  if (props.editMode === 'cut') {
    // Cut mode: darken the parts that will be removed (beginning and end)
    // Left mask
    if (selStartX > 0) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)'
      ctx.fillRect(0, 0, Math.max(0, selStartX), height)
    }

    // Right mask
    if (selEndX < width) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.2)'
      ctx.fillRect(selEndX, 0, width - selEndX, height)
    }
  } else {
    // Trim mode: darken the selection that will be removed
    ctx.fillStyle = 'rgba(0, 0, 0, 0.2)'
    ctx.fillRect(selStartX, 0, selEndX - selStartX, height)
  }
  
  // Draw markers
  const markerColor = cachedColors.markerColor
  
  // Start marker
  drawMarker(ctx, selStartX, height, markerColor, 'start')
  
  // End marker
  drawMarker(ctx, selEndX, height, markerColor, 'end')
  
  // Draw playhead
  const playheadX = timeToPixel(props.currentTime)
  if (playheadX >= 0 && playheadX <= width) {
    // Use pre-darkened color for hover instead of expensive ctx.filter
    const isPlayheadHovered = hoveredMarker.value === 'playhead'
    const playheadColor = isPlayheadHovered ? cachedColors.playheadColorHover : cachedColors.playheadColor

    ctx.strokeStyle = playheadColor
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(playheadX, 0)
    ctx.lineTo(playheadX, height)
    ctx.stroke()

    // Playhead triangle
    ctx.fillStyle = playheadColor
    ctx.beginPath()
    ctx.moveTo(playheadX - 6, 0)
    ctx.lineTo(playheadX + 6, 0)
    ctx.lineTo(playheadX, 10)
    ctx.closePath()
    ctx.fill()
  }
  
  // Reset scale
  ctx.setTransform(1, 0, 0, 1, 0, 0)
}

// Draw a marker handle
const drawMarker = (
  ctx: CanvasRenderingContext2D,
  x: number,
  height: number,
  color: string,
  type: 'start' | 'end'
) => {
  if (x < -20 || x > canvasWidth.value + 20) return

  // Use pre-darkened color for hover instead of expensive ctx.filter
  const isHovered = hoveredMarker.value === type
  ctx.strokeStyle = isHovered ? cachedColors.markerColorHover : color
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(x, 0)
  ctx.lineTo(x, height)
  ctx.stroke()
}

// Check if mouse is near a marker or playhead
const isNearMarker = (x: number): boolean => {
  const startX = timeToPixel(props.selection.start)
  const endX = timeToPixel(props.selection.end)
  const playheadX = timeToPixel(props.currentTime)
  const threshold = 5
  
  return Math.abs(x - startX) < threshold || 
         Math.abs(x - endX) < threshold || 
         Math.abs(x - playheadX) < threshold
}

// Check if click is on a marker (anywhere on the vertical line)
const getMarkerAtPosition = (x: number): 'start' | 'end' | 'playhead' | null => {
  const startX = timeToPixel(props.selection.start)
  const endX = timeToPixel(props.selection.end)
  const playheadX = timeToPixel(props.currentTime)
  const threshold = 5

  // Check end marker first (highest priority when overlapping)
  if (Math.abs(x - endX) < threshold) {
    return 'end'
  }

  // Check start marker (higher priority than playhead)
  if (Math.abs(x - startX) < threshold) {
    return 'start'
  }

  // Check playhead last
  if (Math.abs(x - playheadX) < threshold) {
    return 'playhead'
  }

  return null
}

// Mouse/Touch event handlers
const handlePointerDown = (event: MouseEvent | TouchEvent) => {
  const rect = canvasRef.value?.getBoundingClientRect()
  if (!rect) return
  
  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
  const x = clientX - rect.left
  
  // Check for marker
  const marker = getMarkerAtPosition(x)
  
  if (marker) {
    isDragging.value = true
    dragTarget.value = marker
    dragStartX.value = x
    dragStartClientX.value = clientX
    dragStartSelection.value = { ...props.selection }
    // Store the initial time value for precise relative dragging
    if (marker === 'start') {
      dragStartTime.value = props.selection.start
    } else if (marker === 'end') {
      dragStartTime.value = props.selection.end
    } else if (marker === 'playhead') {
      dragStartTime.value = props.currentTime
    }
    // Attach document-level listeners for drag tracking
    document.addEventListener('mousemove', handleDragMove)
    document.addEventListener('touchmove', handleDragMove, { passive: false })
  } else {
    // Check if click is in selection region
    const startX = timeToPixel(props.selection.start)
    const endX = timeToPixel(props.selection.end)
    const time = pixelToTime(x)
    
    if (props.editMode === 'cut') {
      // Cut mode: click in selection seeks, click outside starts new selection
      if (x >= startX && x <= endX) {
        // Seek to position within selection
        emit('seek', time)
      } else {
        // Start new selection
        isDragging.value = true
        dragTarget.value = 'end'
        emit('update:selection', { start: time, end: time })
        dragStartSelection.value = { start: time, end: time }
        dragStartClientX.value = clientX
        dragStartTime.value = time
        // Attach document-level listeners for drag tracking
        document.addEventListener('mousemove', handleDragMove)
        document.addEventListener('touchmove', handleDragMove, { passive: false })
      }
    } else {
      // Trim mode: click in kept regions (outside selection) seeks, click in selection starts new selection
      if (x < startX || x > endX) {
        // Seek to position in kept region
        emit('seek', time)
      } else {
        // Click in trimmed region - start new selection
        isDragging.value = true
        dragTarget.value = 'end'
        emit('update:selection', { start: time, end: time })
        dragStartSelection.value = { start: time, end: time }
        dragStartClientX.value = clientX
        dragStartTime.value = time
        // Attach document-level listeners for drag tracking
        document.addEventListener('mousemove', handleDragMove)
        document.addEventListener('touchmove', handleDragMove, { passive: false })
      }
    }
  }
  
  // Touch handling - no zoom functionality
}

// Canvas hover handler - only for cursor changes when not dragging
const handlePointerMove = (event: MouseEvent | TouchEvent) => {
  // Skip if dragging - document-level handler takes over
  if (isDragging.value) return
  
  const rect = canvasRef.value?.getBoundingClientRect()
  if (!rect) return
  
  // Update cursor based on hover position
  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
  const x = clientX - rect.left
  const marker = getMarkerAtPosition(x)
  const nearMarker = isNearMarker(x)

  // Update hovered marker state for visual feedback
  if (hoveredMarker.value !== marker) {
    hoveredMarker.value = marker
    scheduleRender()
  }

  if (canvasRef.value) {
    canvasRef.value.style.cursor = (marker || nearMarker) ? 'col-resize' : 'pointer'
  }
}

// Handle mouse leaving the canvas
const handlePointerLeave = () => {
  if (hoveredMarker.value !== null) {
    hoveredMarker.value = null
    scheduleRender()
  }
}

// Handle label hover events
const handleLabelMouseEnter = (label: 'start' | 'end') => {
  hoveredLabel.value = label
}

const handleLabelMouseLeave = () => {
  hoveredLabel.value = null
}

// Handle label mousedown - prepare for potential drag or click
const handleLabelMouseDown = (event: MouseEvent, label: 'start' | 'end') => {
  // Don't interfere with editing mode
  if (editingLabel.value) return
  
  event.preventDefault()
  labelDragPending.value = label
  labelDragStartX.value = event.clientX
  
  // Store drag start info in case we start dragging
  dragStartClientX.value = event.clientX
  dragStartSelection.value = { ...props.selection }
  dragStartTime.value = label === 'start' ? props.selection.start : props.selection.end
  
  // Add document-level listeners
  document.addEventListener('mousemove', handleLabelDragMove)
  document.addEventListener('mouseup', handleLabelMouseUp)
}

// Handle label drag move - detect if we've moved past threshold
const handleLabelDragMove = (event: MouseEvent) => {
  if (!labelDragPending.value) return
  
  const deltaX = Math.abs(event.clientX - labelDragStartX.value)
  
  // If we've moved past threshold, start actual dragging
  if (deltaX >= DRAG_THRESHOLD && !isDragging.value) {
    isDragging.value = true
    dragTarget.value = labelDragPending.value
    labelDragPending.value = null
    // Let the existing handleDragMove take over
    handleDragMove(event)
    // Transfer to main drag handlers
    document.removeEventListener('mousemove', handleLabelDragMove)
    document.removeEventListener('mouseup', handleLabelMouseUp)
    document.addEventListener('mousemove', handleDragMove)
  }
}

// Handle label mouseup - if we haven't started dragging, it's a click (edit)
const handleLabelMouseUp = () => {
  const label = labelDragPending.value
  labelDragPending.value = null
  
  // Clean up listeners
  document.removeEventListener('mousemove', handleLabelDragMove)
  document.removeEventListener('mouseup', handleLabelMouseUp)
  
  // If we haven't transitioned to dragging, treat as click
  if (label && !isDragging.value) {
    startEditingLabel(label)
  }
}

// Start editing a label
const startEditingLabel = (label: 'start' | 'end') => {
  editingLabel.value = label
  const time = label === 'start' ? props.selection.start : props.selection.end
  editInputValue.value = formatTime(time)
  // Focus the input after Vue updates the DOM
  setTimeout(() => {
    editInputRef.value?.focus()
    editInputRef.value?.select()
  }, 0)
}

// Parse time string to seconds
// Supports formats: MM:SS.m, SS.m, MM:SS, SS
const parseTimeInput = (input: string): number | null => {
  const trimmed = input.trim()
  
  // Try MM:SS.m or MM:SS format
  const colonMatch = trimmed.match(/^(\d{1,2}):(\d{1,2})(?:\.(\d))?$/)
  if (colonMatch) {
    const mins = parseInt(colonMatch[1], 10)
    const secs = parseInt(colonMatch[2], 10)
    const ms = colonMatch[3] ? parseInt(colonMatch[3], 10) : 0
    if (secs >= 60) return null
    return mins * 60 + secs + ms / 10
  }
  
  // Try SS.m or just SS format (no colon)
  const secMatch = trimmed.match(/^(\d+)(?:\.(\d))?$/)
  if (secMatch) {
    const secs = parseInt(secMatch[1], 10)
    const ms = secMatch[2] ? parseInt(secMatch[2], 10) : 0
    return secs + ms / 10
  }
  
  return null
}

// Handle input keydown events
const handleEditKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Enter') {
    commitEdit()
  } else if (event.key === 'Escape') {
    cancelEdit()
  }
}

// Commit the edit
const commitEdit = () => {
  if (!editingLabel.value) return
  
  const newTime = parseTimeInput(editInputValue.value)
  if (newTime === null || newTime < 0 || newTime > props.duration) {
    // Invalid input, just cancel
    cancelEdit()
    return
  }
  
  const label = editingLabel.value
  let newSelection = { ...props.selection }
  
  if (label === 'start') {
    // Ensure start doesn't go past end
    newSelection.start = Math.min(newTime, newSelection.end - 0.1)
    newSelection.start = Math.max(0, newSelection.start)
  } else {
    // Ensure end doesn't go before start
    newSelection.end = Math.max(newTime, newSelection.start + 0.1)
    newSelection.end = Math.min(props.duration, newSelection.end)
  }
  
  emit('update:selection', newSelection)
  editingLabel.value = null
}

// Cancel the edit
const cancelEdit = () => {
  editingLabel.value = null
}

// Document-level drag handler - tracks mouse even outside canvas
// Uses relative movement from drag start for sub-pixel precision
const handleDragMove = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return
  
  const rect = canvasRef.value?.getBoundingClientRect()
  if (!rect) return

  if (event.cancelable) {
    event.preventDefault()
  }
  
  const clientX = 'touches' in event ? event.touches[0].clientX : event.clientX
  
  // Calculate time using relative movement from drag start for better precision
  // This uses the fractional clientX values for sub-pixel accuracy
  const deltaX = clientX - dragStartClientX.value
  const deltaTime = (deltaX / canvasWidth.value) * props.duration
  const time = Math.max(0, Math.min(props.duration, dragStartTime.value + deltaTime))
  
  if (dragTarget.value === 'playhead') {
    // When dragging playhead, seek to the new position
    emit('seek', time)
  } else {
    let newSelection = { ...props.selection }
    
    if (dragTarget.value === 'start') {
      newSelection.start = Math.min(time, newSelection.end - 0.1)
    } else if (dragTarget.value === 'end') {
      newSelection.end = Math.max(time, newSelection.start + 0.1)
    }
    
    emit('update:selection', newSelection)
  }
}

const handlePointerUp = () => {
  if (isDragging.value) {
    // Remove document-level drag listeners
    document.removeEventListener('mousemove', handleDragMove)
    document.removeEventListener('touchmove', handleDragMove)
  }
  isDragging.value = false
  dragTarget.value = null
  // Note: don't clear labelDragPending here - handleLabelMouseUp handles that
}

// Wheel handler - no zoom functionality
const handleWheel = () => {
  // Allow default browser behavior for scrolling
  // No zoom functionality implemented
}

// Debounced resize handler
let resizeTimer: ReturnType<typeof setTimeout> | null = null
const handleResize = () => {
  if (resizeTimer) clearTimeout(resizeTimer)
  resizeTimer = setTimeout(updateCanvasSize, 100)
}

// Watch for changes
watch([() => props.waveformData, () => props.selection, () => props.currentTime, () => props.editMode], () => {
  scheduleRender()
})

// Lifecycle
onMounted(() => {
  refreshCachedColors()
  updateCanvasSize()
  window.addEventListener('resize', handleResize)
  document.addEventListener('mouseup', handlePointerUp)
  document.addEventListener('touchend', handlePointerUp)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
  if (resizeTimer) clearTimeout(resizeTimer)
  document.removeEventListener('mouseup', handlePointerUp)
  document.removeEventListener('touchend', handlePointerUp)
  // Clean up drag listeners in case component is destroyed while dragging
  document.removeEventListener('mousemove', handleDragMove)
  document.removeEventListener('touchmove', handleDragMove)
  // Clean up label drag listeners
  document.removeEventListener('mousemove', handleLabelDragMove)
  document.removeEventListener('mouseup', handleLabelMouseUp)
})
</script>

<template>
  <div ref="containerRef" class="waveform-container">
    <!-- Filename overlay -->
    <div v-if="filename" class="filename-overlay">
      {{ filename }}
    </div>
    
    <!-- Time labels with connecting line -->
    <div class="selection-time-row">
      <!-- Start time label -->
      <div 
        class="time-label time-label-start" 
        :class="{ 'hovered': hoveredMarker === 'start' || hoveredLabel === 'start' }"
        :style="{ 
          left: `${(selection.start / duration) * 100}%`,
          transform: startLabelTransform
        }"
        @mouseenter="handleLabelMouseEnter('start')"
        @mouseleave="handleLabelMouseLeave"
        @mousedown="handleLabelMouseDown($event, 'start')">
        <template v-if="editingLabel === 'start'">
          <input
            ref="editInputRef"
            v-model="editInputValue"
            class="time-input"
            type="text"
            @keydown="handleEditKeydown"
            @blur="commitEdit"
            @click.stop
          />
        </template>
        <template v-else>
          {{ formatTime(selection.start) }}
        </template>
      </div>
      
      <!-- CUT MODE: Connecting line with duration in the middle (between start and end) -->
      <div
        v-if="props.editMode === 'cut'"
        class="duration-connector"
        :style="{
          left: `${(selection.start / duration) * 100}%`,
          width: `${((selection.end - selection.start) / duration) * 100}%`
        }">
        <div class="connector-line connector-line-left"></div>
        <div class="duration-label" :class="{ 'duration-label-below': durationLabelPosition === 'below' }">
          {{ formatDuration(displayedDuration) }}
        </div>
        <div class="connector-line connector-line-right"></div>
      </div>

      <!-- TRIM MODE: Duration label with minus sign (centered above selection) -->
      <div
        v-if="props.editMode === 'trim'"
        class="duration-connector trim-duration"
        :style="{
          left: `${(selection.start / duration) * 100}%`,
          width: `${((selection.end - selection.start) / duration) * 100}%`
        }">
        <div class="duration-label duration-label-trim" :class="{ 'duration-label-below': durationLabelPosition === 'below' }">
          -{{ formatDuration(displayedDuration) }}
        </div>
      </div>

      <!-- TRIM MODE: Left connector (from 0 to start marker) -->
      <div
        v-if="props.editMode === 'trim' && selection.start > 0"
        class="duration-connector trim-connector-left"
        :style="{
          left: '0%',
          width: `${(selection.start / duration) * 100}%`
        }">
        <div class="connector-line"></div>
      </div>

      <!-- TRIM MODE: Right connector (from end marker to duration) -->
      <div
        v-if="props.editMode === 'trim' && selection.end < duration"
        class="duration-connector trim-connector-right"
        :style="{
          left: `${(selection.end / duration) * 100}%`,
          width: `${((duration - selection.end) / duration) * 100}%`
        }">
        <div class="connector-line"></div>
      </div>
      
      <!-- End time label -->
      <div 
        class="time-label time-label-end" 
        :class="{ 'hovered': hoveredMarker === 'end' || hoveredLabel === 'end' }"
        :style="{ 
          left: `${(selection.end / duration) * 100}%`,
          transform: endLabelTransform
        }"
        @mouseenter="handleLabelMouseEnter('end')"
        @mouseleave="handleLabelMouseLeave"
        @mousedown="handleLabelMouseDown($event, 'end')">
        <template v-if="editingLabel === 'end'">
          <input
            ref="editInputRef"
            v-model="editInputValue"
            class="time-input"
            type="text"
            @keydown="handleEditKeydown"
            @blur="commitEdit"
            @click.stop
          />
        </template>
        <template v-else>
          {{ formatTime(selection.end) }}
        </template>
      </div>
    </div>
    
    <!-- Current time label (playhead) -->
    <div 
      class="time-label time-label-current" 
      :class="{ 'hovered': hoveredMarker === 'playhead' }"
      :style="{ left: `${(currentTime / duration) * 100}%` }">
      {{ formatTime(currentTime) }}
    </div>
    
    <canvas
      ref="canvasRef"
      class="waveform-canvas"
      @mousedown="handlePointerDown"
      @mousemove="handlePointerMove"
      @mouseleave="handlePointerLeave"
      @touchstart.passive="handlePointerDown"
      @touchmove="handlePointerMove"
      @wheel="handleWheel"
    ></canvas>
  </div>
</template>

<style scoped>
.waveform-container {
  width: 100%;
  position: relative;
  user-select: none;
  touch-action: none;
  /* Reserve space for canvas to prevent CLS */
  min-height: 150px;
  /* CSS containment for better performance */
  contain: layout style;
}

.waveform-canvas {
  width: 100%;
  /* Explicit aspect-ratio prevents layout shift during canvas initialization */
  aspect-ratio: 16 / 3;
  min-height: 100px;
  max-height: 200px;
  border-radius: var(--radius-md);
  cursor: pointer;
  /* Prevent FOUC */
  background: var(--waveform-background, #f8f8f8);
}

.filename-overlay {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 500;
  pointer-events: none;
  z-index: 10;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  /* Filenames should always be LTR regardless of page language */
  direction: ltr;
  unicode-bidi: embed;
}

.time-label {
  position: absolute;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.85);
  color: white;
  padding: 0 0.25rem;
  border-radius: var(--radius-sm);
  font-size: 0.8rem;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-weight: 600;
  pointer-events: none;
  z-index: 10;
  white-space: nowrap;
}

.selection-time-row {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  height: 36px;
  pointer-events: none;
  z-index: 10;
}

.selection-time-row .time-label {
  top: 0;
}

.time-label-start {
  background: var(--marker-color);
  color: white;
  pointer-events: auto;
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.time-label-end {
  background: var(--marker-color);
  color: white;
  pointer-events: auto;
  cursor: pointer;
  transition: background-color var(--transition-fast);
}

.time-label-start.hovered,
.time-label-end.hovered {
  background: color-mix(in srgb, var(--marker-color) 65%, black);
}

.time-input {
  background: transparent;
  border: none;
  color: white;
  font-size: 0.8rem;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-weight: 600;
  width: 5.5em;
  text-align: center;
  outline: none;
  padding: 0;
  margin: 0;
}

.time-label-current.hovered {
  background: color-mix(in srgb, var(--error-color) 65%, black);
}

.duration-connector {
  position: absolute;
  top: 5px;
  display: flex;
  align-items: center;
  height: 12px;
  pointer-events: none;
}

.connector-line {
  flex: 1;
  height: 1px;
  background: var(--marker-color);
  min-width: 4px;
}

.connector-line-left {
  margin-left: 0;
}

.connector-line-right {
  margin-right: 0;
}

.duration-label {
  padding: 0 0.375rem;
  border: 1px solid var(--marker-color);
  border-radius: var(--radius-sm);
  font-size: 0.8rem;
  font-family: 'SF Mono', 'Monaco', 'Consolas', monospace;
  font-weight: 600;
  color: var(--text-primary);
  background: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  line-height: 1.4;
  box-shadow: var(--shadow-sm);
}

.duration-label-below {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
}

/* Trim mode styles */
.trim-duration {
  justify-content: center;
}

.duration-label-trim {
  border-color: var(--warning-color);
  color: var(--warning-color);
  /* LTR for proper number display (minus on left of number) */
  direction: ltr;
}

.trim-connector-left,
.trim-connector-right {
  justify-content: stretch;
}

.trim-connector-left .connector-line,
.trim-connector-right .connector-line {
  background: var(--marker-color);
}

.time-label-current {
  background: var(--error-color);
  color: white;
  top: auto;
  bottom: 100%;
  margin-top: 0;
  margin-bottom: 0;
}
</style>
